import { withLDConsumer } from "launchdarkly-react-client-sdk"
import { DefaultLayout } from "layouts"
import { cn } from "lib/utils"
import { Suspense } from "react"
import { useRoutes } from "react-router-dom"
import lazyWithRetry from "utils/lazyWithRetry"
import { Layout } from "../layouts/Layout"
import PrivateRoute from "./PrivateRoute"

import useUserStore from "stores/user"

const ErrorPageNotFound = lazyWithRetry(
  () => import("pages/error/PageNotFound")
)

// auth
import { Logout } from "pages/auth/Logout"
const Login = lazyWithRetry(() => import("pages/auth/Login"))
const Forgot = lazyWithRetry(() => import("pages/auth/Forgot"))
const Reset = lazyWithRetry(() => import("pages/auth/Reset"))
const ChangeEmail = lazyWithRetry(() => import("pages/auth/ChangeEmail"))
const Activate = lazyWithRetry(() => import("pages/auth/Activate"))
const Profile = lazyWithRetry(() => import("pages/auth/Profile"))

const Dashboard = lazyWithRetry(() => import("pages/dashboard/dashboard"))
const QuotingHistory = lazyWithRetry(() => import("pages/quotingHistory"))
const Customers = lazyWithRetry(() => import("pages/customers/Customers"))
const CustomerDetails = lazyWithRetry(() => import("pages/customerDetails"))

const TankEdit = lazyWithRetry(() => import("pages/customers/TankEdit"))
const CustomerEdit = lazyWithRetry(() => import("pages/customers/CustomerEdit"))
const QBCustomers = lazyWithRetry(() => import("pages/qb_customers/Customers"))
const QBItems = lazyWithRetry(() => import("pages/qb_items/"))
const QBClasses = lazyWithRetry(() => import("pages/qb_classes/"))
const QBDepartments = lazyWithRetry(() => import("pages/qb_departments/"))
const TaxPage = lazyWithRetry(() => import("pages/TaxPage/"))
const SiteDistances = lazyWithRetry(
  () => import("pages/customers/SiteDistancesModal")
)

const Quotations = lazyWithRetry(() => import("pages/quotations"))
const SendQuotations = lazyWithRetry(() => import("pages/send_quotations"))
const ContractAllocation = lazyWithRetry(
  () => import("pages/contract_site_list/")
)
const Planner = lazyWithRetry(() => import("pages/planner"))
const Contracts = lazyWithRetry(() => import("pages/Contracts"))
const FreightBilling = lazyWithRetry(() => import("pages/freightBilling"))
const DriverPay = lazyWithRetry(() => import("pages/freightBilling/DriverPay"))
const CustomSuppliers = lazyWithRetry(() => import("pages/customSuppliers"))
const PricingPage = lazyWithRetry(() => import("pages/PricingPage"))
const FeeManagement = lazyWithRetry(() => import("pages/FeeManagement"))
const FreightBillingDetails = lazyWithRetry(
  () => import("pages/freightBillingDetails")
)
const InvoiceDetails = lazyWithRetry(() => import("pages/invoices/details"))
const DriverPaySetup = lazyWithRetry(() => import("pages/driverPayRateSetup"))
const FreightBillingSetup = lazyWithRetry(
  () => import("pages/freightBillingSetup")
)

const Products = lazyWithRetry(() => import("pages/products/"))
const ProductModal = lazyWithRetry(() => import("pages/products/ProductModal"))
const Terminals = lazyWithRetry(() => import("pages/terminals/Terminals"))
const TerminalModal = lazyWithRetry(
  () => import("pages/terminals/TerminalModal")
)

const EMailNotifications = lazyWithRetry(
  () => import("pages/EMailNotifications")
)
const CustomerGroups = lazyWithRetry(() => import("pages/CustomerGroups"))
const Groups = lazyWithRetry(() => import("pages/Groups"))
const CustomerGroupModal = lazyWithRetry(
  () => import("pages/CustomerGroups/CustomerGroupModal")
)
const Drivers = lazyWithRetry(() => import("pages/drivers/Drivers"))
const CommonCarriers = lazyWithRetry(() => import("pages/CommonCarriers/"))
const DriversModal = lazyWithRetry(() => import("pages/drivers/DriversModal"))
const Trucks = lazyWithRetry(() => import("pages/trucks/Trucks"))
const Outages = lazyWithRetry(() => import("pages/outages/"))
const LoadingNumbers = lazyWithRetry(() => import("pages/loadingNumbers/"))
const Tanks = lazyWithRetry(() => import("pages/tanks/"))
const Trailers = lazyWithRetry(() => import("pages/trailers/Trailers"))
const Suppliers = lazyWithRetry(() => import("pages/suppliers/Suppliers"))
const SupplierModal = lazyWithRetry(
  () => import("pages/suppliers/SupplierModal")
)

const DispatchCenter = lazyWithRetry(
  () => import("pages/dispatchCenter/DispatchCenter")
)
const Organiser = lazyWithRetry(() => import("pages/dispatchCenter/Organiser"))
const Loads = lazyWithRetry(() => import("pages/loads/Loads"))
const SimpleLoads = lazyWithRetry(() => import("pages/simpleLoads/"))
const Deliveries = lazyWithRetry(() => import("pages/deliveries/"))
const SiteExport = lazyWithRetry(() => import("pages/siteExport/"))

const LoadsCreateForm = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "CreateLoadComponent" */ "pages/loads/CreateLoadCard/"
    )
)
const EditSuppliersForm = lazyWithRetry(
  () => import("pages/loads/EditSuppliers")
)
const LoadDetailsPage = lazyWithRetry(
  () => import("pages/loads/LoadDetailsPage")
)

const BolModal = lazyWithRetry(() => import("pages/loads/BoLEdit"))
const TicketModal = lazyWithRetry(() => import("pages/loads/TicketEdit"))
const PickupForm = lazyWithRetry(() => import("pages/loads/EditPickupCard"))
const DeliveryForm = lazyWithRetry(() => import("pages/loads/EditDeliveryCard"))

const OrderTracking = lazyWithRetry(() => import("pages/loadTracker/"))
const DispatchMap = lazyWithRetry(() => import("pages/dispatch_map/"))
const LoadDebug = lazyWithRetry(() => import("pages/loadDebug/"))
const InventoryManagement = lazyWithRetry(
  () => import("pages/InventoryManagement")
)
const MapView = lazyWithRetry(() => import("pages/MapView/"))
const Paperwork = lazyWithRetry(() => import("pages/loadPaperwork/"))

const ScheduleLoads = lazyWithRetry(() => import("pages/scheduleLoads"))
const DriverShifts = lazyWithRetry(() => import("pages/DriverShifts"))
const DriverChecklistPreview = lazyWithRetry(
  () => import("pages/DriverChecklistPreview")
)
const MobileFueling = lazyWithRetry(() => import("pages/MobileFueling"))
const ScoreCard = lazyWithRetry(() => import("pages/ScoreCard"))
const MobileFuelingLoad = lazyWithRetry(
  () => import("pages/MobileFueling/loads")
)
const MobileFuelingDrivers = lazyWithRetry(
  () => import("pages/MobileFueling/driversView")
)

const SupplyOverview = lazyWithRetry(() => import("pages/SupplyOverview"))
const ArbitrageMap = lazyWithRetry(() => import("pages/ArbitrageMap"))

// DashFill routes
const DashFillIndex = lazyWithRetry(() => import("pages/reconciliation/"))
const DashFillReview = lazyWithRetry(
  () => import("pages/reconciliation/document-review")
)

const Swagger = lazyWithRetry(() => import("pages/swagger/Swagger"))
const TerminalsViewV2 = lazyWithRetry(() =>
  import("pages/app/terminals/list/terminals").then((module) => ({
    default: module.Terminals,
  }))
)
const TerminalsCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/terminals/create-view").then((module) => ({
    default: module.TerminalCreateView,
  }))
)
const TerminalsEditViewV2 = lazyWithRetry(() =>
  import("pages/app/terminals/edit-view").then((module) => ({
    default: module.TerminalEditView,
  }))
)
const CustomersViewV2 = lazyWithRetry(() =>
  import("pages/app/customers").then((module) => ({
    default: module.CustomersView,
  }))
)

const TrucksViewV2 = lazyWithRetry(() =>
  import("pages/app/trucks/list/").then((module) => ({
    default: module.TrucksView,
  }))
)

const TrucksCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/trucks/create-view").then((module) => ({
    default: module.TrucksCreateView,
  }))
)
const TrucksEditViewV2 = lazyWithRetry(() =>
  import("pages/app/trucks/edit-view").then((module) => ({
    default: module.TrucksEditView,
  }))
)

const LoadingNumbersViewV2 = lazyWithRetry(() =>
  import("pages/app/loading-numbers/list/list").then((module) => ({
    default: module.LoadingNumbersView,
  }))
)

const LoadingNumbersCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/loading-numbers/create-view/create-view").then(
    (module) => ({
      default: module.LoadingNumbersCreateView,
    })
  )
)
const LoadingNumbersEditViewV2 = lazyWithRetry(() =>
  import("pages/app/loading-numbers/edit-view/edit-view").then((module) => ({
    default: module.LoadingNumbersEditView,
  }))
)
const CommonCarriersViewV2 = lazyWithRetry(() =>
  import("pages/app/common-carriers/list").then((module) => ({
    default: module.CommonCarriersView,
  }))
)

const CommonCarriersCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/common-carriers/create-view").then((module) => ({
    default: module.CommonCarriersCreateView,
  }))
)
const CommonCarriersEditViewV2 = lazyWithRetry(() =>
  import("pages/app/common-carriers/edit-view").then((module) => ({
    default: module.CommonCarriersEditView,
  }))
)

const UsersViewV2 = lazyWithRetry(() =>
  import("pages/app/users/list/list").then((module) => ({
    default: module.UsersView,
  }))
)

const UsersCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/users/create-view/create-view").then((module) => ({
    default: module.UsersCreateView,
  }))
)
const UsersItemViewV2 = lazyWithRetry(() =>
  import("pages/app/users/details-view/details-view").then((module) => ({
    default: module.UsersItemView,
  }))
)
const UsersEditViewV2 = lazyWithRetry(() =>
  import("pages/app/users/edit-view/edit-view").then((module) => ({
    default: module.UsersEditView,
  }))
)

const CustomerListViewV2 = lazyWithRetry(() =>
  import("pages/app/customers/list/customers").then((module) => ({
    default: module.Customers,
  }))
)
const CustomerDetailsViewV2 = lazyWithRetry(() =>
  import("pages/app/customers/detail-view").then((module) => ({
    default: module.CustomerDetailsView,
  }))
)
const CustomerCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/customers/create-view").then((module) => ({
    default: module.CustomerCreateView,
  }))
)
const CustomerEditViewV2 = lazyWithRetry(() =>
  import("pages/app/customers/edit-view").then((module) => ({
    default: module.CustomerEditView,
  }))
)
const CustomerLocationsV2 = lazyWithRetry(() =>
  import("pages/app/customers/locations").then((module) => ({
    default: module.CustomerLocations,
  }))
)
const TrailersListViewV2 = lazyWithRetry(() =>
  import("pages/app/trailers/list/trailers").then((module) => ({
    default: module.Trailers,
  }))
)

const TrailerCreateViewV2 = lazyWithRetry(() =>
  import("pages/app/trailers/create-view").then((module) => ({
    default: module.TrailerCreateView,
  }))
)
const TrailerEditViewV2 = lazyWithRetry(() =>
  import("pages/app/trailers/edit-view/").then((module) => ({
    default: module.TrailerEditView,
  }))
)
const CustomerLocationsCreateV2 = lazyWithRetry(() =>
  import("pages/app/customers/locations/create-view").then((module) => ({
    default: module.CustomerLocationCreateView,
  }))
)
const CustomerLocationsEditV2 = lazyWithRetry(() =>
  import("pages/app/customers/locations/edit-view").then((module) => ({
    default: module.CustomerLocationEditView,
  }))
)
const InventoryManagementV2 = lazyWithRetry(() =>
  import("pages/app/inventory/inventory").then((module) => ({
    default: module.Inventory,
  }))
)
const ProductsViewV2 = lazyWithRetry(() =>
  import("pages/app/products/list/list").then((module) => ({
    default: module.ProductsView,
  }))
)

const DynamicDashboard = lazyWithRetry(() => import("pages/dashboards"))

const loading = () => <></>

type LoadComponentProps = {
  component: any
  isLegacyPage?: boolean
}

const LoadComponent = ({
  component: Component,
  isLegacyPage,
}: LoadComponentProps) => (
  <Suspense fallback={loading()}>
    <div className={cn("w-full", isLegacyPage && "bootstrap-scope p-4")}>
      <Component />
    </div>
  </Suspense>
)

const AllRoutes = () => {
  const isCustomer = useUserStore((state) => state.isCustomer)
  const DefaultComponent = isCustomer ? FreightBilling : DispatchCenter
  return useRoutes([
    {
      path: "/",
      element: <PrivateRoute component={Layout} />,
      children: [
        {
          path: "/",
          element: <LoadComponent component={DefaultComponent} />,
          children: [
            {
              path: "create-load",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
            {
              path: "load/:load_id/deliveries/create",
              element: <LoadComponent component={DeliveryForm} isLegacyPage />,
            },
          ],
        },
        {
          path: "/organiser",
          element: <LoadComponent component={Organiser} isLegacyPage />,
        },
        {
          path: "/contract_allocation",
          element: (
            <LoadComponent component={ContractAllocation} isLegacyPage />
          ),
        },
        {
          path: "reconciliation",
          element: <LoadComponent component={DashFillIndex} />,
        },
        {
          path: "reconciliation/review",
          element: <LoadComponent component={DashFillReview} />,
        },
        {
          path: "reconciliation/review/:document_id",
          element: <LoadComponent component={DashFillReview} />,
        },
        {
          path: "inventory",
          element: (
            <LoadComponent component={InventoryManagement} isLegacyPage />
          ),
          children: [
            {
              path: "create-load",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "/map-view",
          element: <LoadComponent component={MapView} isLegacyPage />,
        },
        {
          path: "paperwork",
          element: <LoadComponent component={Paperwork} isLegacyPage />,
        },
        {
          path: "pricing",
          element: <LoadComponent component={PricingPage} isLegacyPage />,
        },
        {
          path: "fees",
          element: <LoadComponent component={FeeManagement} isLegacyPage />,
        },
        {
          path: "custom_suppliers",
          element: <LoadComponent component={CustomSuppliers} isLegacyPage />,
        },
        {
          path: "driver-pay",
          element: <LoadComponent component={DriverPay} isLegacyPage />,
        },
        {
          path: "freight_billing",
          element: <LoadComponent component={FreightBilling} isLegacyPage />,
          children: [
            {
              path: "create-load",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "freight_bill/:id",
          element: (
            <LoadComponent component={FreightBillingDetails} isLegacyPage />
          ),
        },
        {
          path: "invoice/:id",
          element: <LoadComponent component={InvoiceDetails} isLegacyPage />,
        },
        {
          path: "freight_billing_setup",
          element: (
            <LoadComponent component={FreightBillingSetup} isLegacyPage />
          ),
        },
        {
          path: "driver_pay_setup",
          element: <LoadComponent component={DriverPaySetup} isLegacyPage />,
        },
        {
          path: "profile",
          element: <LoadComponent component={Profile} isLegacyPage />,
        },
        {
          path: "quotations",
          element: <LoadComponent component={Quotations} isLegacyPage />,
        },
        {
          path: "send_quotations",
          element: <LoadComponent component={SendQuotations} isLegacyPage />,
        },
        {
          path: "contracts",
          element: <LoadComponent component={Contracts} isLegacyPage />,
        },
        {
          path: "contract_allocation",
          element: (
            <LoadComponent component={ContractAllocation} isLegacyPage />
          ),
        },
        {
          path: "quoting_history",
          element: <LoadComponent component={QuotingHistory} isLegacyPage />,
        },
        {
          path: "scorecard",
          element: <LoadComponent component={ScoreCard} isLegacyPage />,
        },
        {
          path: "planner",
          element: <LoadComponent component={Planner} isLegacyPage />,
          children: [
            {
              path: "create-load",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "new_customer_page",
          element: <LoadComponent component={CustomerDetails} isLegacyPage />,
          children: [
            {
              path: "location/:location_id/site_distances",
              element: <LoadComponent component={SiteDistances} isLegacyPage />,
            },
            {
              path: "location/:location_id/tank/",
              element: <LoadComponent component={TankEdit} isLegacyPage />,
            },
            {
              path: "location/:location_id/tank/:tank_id",
              element: <LoadComponent component={TankEdit} isLegacyPage />,
            },
          ],
        },
        {
          path: "customers/:id/edit",
          element: <LoadComponent component={CustomerEdit} isLegacyPage />,
          children: [
            {
              path: "location/:location_id/site_distances",
              element: <LoadComponent component={SiteDistances} isLegacyPage />,
            },
            {
              path: "location/:location_id/tank/",
              element: <LoadComponent component={TankEdit} isLegacyPage />,
            },
            {
              path: "location/:location_id/tank/:tank_id",
              element: <LoadComponent component={TankEdit} isLegacyPage />,
            },
          ],
        },
        {
          path: "customers/create",
          element: <LoadComponent component={CustomerEdit} isLegacyPage />,
        },
        {
          path: "qb_customers",
          element: <LoadComponent component={QBCustomers} isLegacyPage />,
        },
        {
          path: "qb_items",
          element: <LoadComponent component={QBItems} isLegacyPage />,
        },
        {
          path: "qb_classes",
          element: <LoadComponent component={QBClasses} isLegacyPage />,
        },
        {
          path: "qb_departments",
          element: <LoadComponent component={QBDepartments} isLegacyPage />,
        },
        {
          path: "customers",
          element: <LoadComponent component={Customers} isLegacyPage />,
        },
        {
          path: "products",
          element: <LoadComponent component={Products} isLegacyPage />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={ProductModal} isLegacyPage />,
            },
            {
              path: ":id/edit",
              element: <LoadComponent component={ProductModal} isLegacyPage />,
            },
          ],
        },
        {
          path: "tracking",
          element: <LoadComponent component={OrderTracking} isLegacyPage />,
          children: [
            {
              path: "create",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "groups",
          element: <LoadComponent component={Groups} isLegacyPage />,
          children: [
            {
              path: "customer-groups/create",
              element: (
                <LoadComponent component={CustomerGroupModal} isLegacyPage />
              ),
            },
            {
              path: "customer-groups/:id/edit",
              element: (
                <LoadComponent component={CustomerGroupModal} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "customer-groups",
          element: <LoadComponent component={CustomerGroups} isLegacyPage />,
          children: [
            {
              path: "create",
              element: (
                <LoadComponent component={CustomerGroupModal} isLegacyPage />
              ),
            },
            {
              path: ":id/edit",
              element: (
                <LoadComponent component={CustomerGroupModal} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "terminals",
          element: <LoadComponent component={Terminals} isLegacyPage />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={TerminalModal} isLegacyPage />,
            },
            {
              path: ":id/edit",
              element: <LoadComponent component={TerminalModal} isLegacyPage />,
            },
          ],
        },
        {
          path: "email-notifications",
          element: (
            <LoadComponent component={EMailNotifications} isLegacyPage />
          ),
        },
        {
          path: "drivers",
          element: <LoadComponent component={Drivers} isLegacyPage />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={DriversModal} isLegacyPage />,
            },
            {
              path: ":id/edit",
              element: <LoadComponent component={DriversModal} isLegacyPage />,
            },
          ],
        },
        {
          path: "/map-view",
          element: <LoadComponent component={MapView} isLegacyPage />,
        },
        {
          path: "agents",
          element: <LoadComponent component={Drivers} isLegacyPage />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={DriversModal} isLegacyPage />,
            },
            {
              path: ":id/edit",
              element: <LoadComponent component={DriversModal} isLegacyPage />,
            },
          ],
        },
        {
          path: "trucks",
          element: <LoadComponent component={Trucks} isLegacyPage />,
        },
        {
          path: "tax",
          element: <LoadComponent component={TaxPage} isLegacyPage />,
        },
        {
          path: "outages",
          element: <LoadComponent component={Outages} isLegacyPage />,
        },
        {
          path: "loading_numbers",
          element: <LoadComponent component={LoadingNumbers} isLegacyPage />,
        },
        {
          path: "tanks",
          element: <LoadComponent component={Tanks} isLegacyPage />,
        },
        {
          path: "dispatch",
          element: <LoadComponent component={DispatchMap} isLegacyPage />,
        },
        {
          path: "common-carriers",
          element: <LoadComponent component={CommonCarriers} isLegacyPage />,
        },
        {
          path: "trailers",
          element: <LoadComponent component={Trailers} isLegacyPage />,
        },
        {
          path: "suppliers",
          element: <LoadComponent component={Suppliers} isLegacyPage />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={SupplierModal} isLegacyPage />,
            },
            {
              path: ":id/edit",
              element: <LoadComponent component={SupplierModal} isLegacyPage />,
            },
          ],
        },
        {
          path: "loads",
          element: <LoadComponent component={SimpleLoads} isLegacyPage />,
        },
        {
          path: "deliveries",
          element: <LoadComponent component={Deliveries} isLegacyPage />,
        },
        {
          path: "site-export",
          element: <LoadComponent component={SiteExport} isLegacyPage />,
        },
        { path: "old_loads", element: <LoadComponent component={Loads} /> },
        {
          path: "new_loads/:id/debug",
          element: <LoadComponent component={LoadDebug} isLegacyPage />,
        },
        {
          path: "loads",
          element: <LoadComponent component={SimpleLoads} isLegacyPage />,
          children: [
            {
              path: "create",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
            {
              path: ":id/edit",
              element: (
                <LoadComponent component={LoadsCreateForm} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "new_loads/:load_id",
          element: <LoadComponent component={LoadDetailsPage} isLegacyPage />,
          children: [
            {
              path: "pickups/create",
              element: <LoadComponent component={PickupForm} isLegacyPage />,
            },
            {
              path: "pickups/:pickup_id/edit",
              element: <LoadComponent component={PickupForm} isLegacyPage />,
            },
            {
              path: "deliveries/create",
              element: <LoadComponent component={DeliveryForm} isLegacyPage />,
            },
            {
              path: "deliveries/:delivery_id/edit",
              element: <LoadComponent component={DeliveryForm} isLegacyPage />,
            },
            {
              path: "deliveries/:delivery_id/tickets/",
              element: <LoadComponent component={TicketModal} isLegacyPage />,
            },
            {
              path: "deliveries/:delivery_id/tickets/",
              element: <LoadComponent component={TicketModal} isLegacyPage />,
            },
            {
              path: "pickups/:pickup_id/bols/create",
              element: <LoadComponent component={BolModal} isLegacyPage />,
            },
            {
              path: "pickups/:pickup_id/bols/",
              element: <LoadComponent component={BolModal} isLegacyPage />,
            },
            {
              path: "pickups/:pickup_id/bols/:bol_id",
              element: <LoadComponent component={BolModal} isLegacyPage />,
            },
            {
              path: "pickups/:pickup_id/product/:product_id",
              element: (
                <LoadComponent component={EditSuppliersForm} isLegacyPage />
              ),
            },
          ],
        },
        {
          path: "driver-hours",
          element: <LoadComponent component={DriverShifts} />,
        },
        {
          path: "pre-post-trip-reports",
          element: <LoadComponent component={DriverChecklistPreview} />,
        },
        {
          path: "scheduled-loads",
          element: <LoadComponent component={ScheduleLoads} />,
        },
        {
          path: "mobile-fueling",
          element: <LoadComponent component={MobileFueling} />,
        },
        {
          path: "mobile-fueling/loads/",
          element: <LoadComponent component={MobileFuelingLoad} />,
        },
        {
          path: "mobile-fueling/loads/:load_id",
          element: <LoadComponent component={MobileFuelingLoad} />,
        },
        {
          path: "mobile-fueling/drivers",
          element: <LoadComponent component={MobileFuelingDrivers} />,
        },
        {
          path: "/dashboard",
          element: <LoadComponent component={Dashboard} isLegacyPage />,
        },
        {
          path: "supply-management/overview",
          element: <LoadComponent component={SupplyOverview} isLegacyPage />,
        },
        {
          path: "supply-management/arbitrage-map",
          element: <LoadComponent component={ArbitrageMap} />,
        },
        {
          path: "swagger",
          element: <LoadComponent component={Swagger} isLegacyPage />,
        },

        // Routes to redesigned pages

        {
          path: "app/common-carriers",
          element: <LoadComponent component={CommonCarriersViewV2} />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={CommonCarriersCreateViewV2} />,
            },
            {
              path: ":common_carrier_id/edit",
              element: <LoadComponent component={CommonCarriersEditViewV2} />,
            },
          ],
        },
        {
          path: "app/customers",
          element: <LoadComponent component={CustomersViewV2} />,
          children: [
            {
              index: true,
              element: <LoadComponent component={CustomerListViewV2} />,
            },
            {
              path: "create",
              element: <LoadComponent component={CustomerCreateViewV2} />,
            },
            {
              path: ":customer_id",
              element: <LoadComponent component={CustomerDetailsViewV2} />,
            },
            {
              path: ":customer_id/edit",
              element: <LoadComponent component={CustomerEditViewV2} />,
            },
            {
              path: ":customer_id/locations",
              element: <LoadComponent component={CustomerLocationsV2} />,
            },
            {
              path: ":customer_id/locations/create",
              element: <LoadComponent component={CustomerLocationsCreateV2} />,
            },
            {
              path: ":customer_id/locations/:location_id",
              element: <LoadComponent component={CustomerLocationsV2} />,
            },
            {
              path: ":customer_id/locations/:location_id/edit",
              element: <LoadComponent component={CustomerLocationsEditV2} />,
            },
          ],
        },
        {
          path: "app/loading-numbers",
          element: <LoadComponent component={LoadingNumbersViewV2} />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={LoadingNumbersCreateViewV2} />,
            },
            {
              path: ":loading_number_id/edit",
              element: <LoadComponent component={LoadingNumbersEditViewV2} />,
            },
          ],
        },
        {
          path: "app/terminals",
          element: <LoadComponent component={TerminalsViewV2} />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={TerminalsCreateViewV2} />,
            },
            {
              path: ":terminal_id",
              element: <LoadComponent component={TerminalsEditViewV2} />,
            },
          ],
        },
        {
          path: "app/trailers",
          element: <LoadComponent component={TrailersListViewV2} />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={TrailerCreateViewV2} />,
            },
            {
              path: ":trailer_id",
              element: <LoadComponent component={TrailerEditViewV2} />,
            },
          ],
        },
        {
          path: "app/trucks",
          element: <LoadComponent component={TrucksViewV2} />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={TrucksCreateViewV2} />,
            },
            {
              path: ":truck_id/edit",
              element: <LoadComponent component={TrucksEditViewV2} />,
            },
          ],
        },
        {
          path: "app/users",
          element: <LoadComponent component={UsersViewV2} />,
          children: [
            {
              path: "create",
              element: <LoadComponent component={UsersCreateViewV2} />,
            },
            {
              path: ":user_id",
              element: <LoadComponent component={UsersItemViewV2} />,
            },
            {
              path: ":user_id/edit",
              element: <LoadComponent component={UsersEditViewV2} />,
            },
          ],
        },

        // End routes to redesigned pages

        {
          path: "app/products",
          element: <LoadComponent component={ProductsViewV2} />,
        },
        {
          path: "app/inventory",
          element: <LoadComponent component={InventoryManagementV2} />,
          children: [
            {
              path: ":site_id",
              element: <LoadComponent component={InventoryManagementV2} />,
            },
            {
              path: ":site_id/tank/:tank_id",
              element: <LoadComponent component={InventoryManagementV2} />,
            },
          ],
        },
        {
          path: "dashboards/:dashboardId",
          element: <LoadComponent component={DynamicDashboard} />,
        },
      ],
    },
    {
      path: "/",
      element: <DefaultLayout />,
      children: [
        { path: "login", element: <LoadComponent component={Login} /> },
        { path: "forgot", element: <LoadComponent component={Forgot} /> },
        { path: "logout", element: <LoadComponent component={Logout} /> },
        {
          path: "reset/:uid/:token",
          element: <LoadComponent component={Reset} isLegacyPage />,
        },
        {
          path: "activate/:uid/:token",
          element: <LoadComponent component={Activate} isLegacyPage />,
        },
        {
          path: "change-email/:email/:token",
          element: <LoadComponent component={ChangeEmail} isLegacyPage />,
        },
      ],
    },
    {
      path: "*",
      element: <LoadComponent component={ErrorPageNotFound} />,
    },
  ])
}

export default withLDConsumer()(AllRoutes)
